import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryData } from '@/mocks/categories';
import { categories } from '@/mocks/home';

type VideoItem = {
  id: string;
  title: string;
  cover: string;
  duration: string;
  views: string;
  author: string;
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'funny');
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDesc, setCategoryDesc] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const videosPerPage = 6;

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  useEffect(() => {
    const data = categoryData[activeSlug as keyof typeof categoryData];
    if (data) {
      setCategoryName(data.name);
      setCategoryDesc(data.description);
      let allVideos = data.videos;

      if (activeTab === 'hot') {
        allVideos = [...allVideos].sort((a, b) => {
          const numA = parseInt(a.views.replace('万', ''));
          const numB = parseInt(b.views.replace('万', ''));
          return numB - numA;
        });
      } else if (activeTab === 'new') {
        allVideos = [...allVideos].reverse();
      }

      setVideos(allVideos);
    }
  }, [activeSlug, activeTab]);

  useEffect(() => {
    document.title = `${categoryName || '分类'} - 快猫短视频`;
  }, [categoryName]);

  const totalPages = Math.ceil(videos.length / videosPerPage);
  const paginatedVideos = videos.slice(
    (currentPage - 1) * videosPerPage,
    currentPage * videosPerPage
  );

  const tabs = [
    { key: 'all', label: '全部' },
    { key: 'hot', label: '最热' },
    { key: 'new', label: '最新' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 快猫短视频`,
    description: categoryDesc,
    url: `https://www.kuaimao.com/category/${activeSlug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '内容分类', to: '/category/funny' },
              { label: categoryName },
            ]}
          />

          <section className="w-full py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500">{categoryDesc}</p>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => {
                      setActiveSlug(cat.slug);
                      setCurrentPage(1);
                    }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-2 mb-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setCurrentPage(1);
                    }}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {paginatedVideos.map((video) => (
                  <div
                    key={video.id}
                    className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={video.cover}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded-md">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-play-fill text-background-50 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{video.author}</span>
                        <span>{video.views}次播放</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}